import { visibleFirms } from '@/demo/scoping'
import { MODULE_REGISTRY, perClientModules, type ModuleEntry, type ModuleLevel } from '@/modules/architecture'
import {
  ANCHOR_FIRM_ID,
  clientsForFirm,
  moduleStateFor,
  type Client,
  type Firm,
  type ModuleInstallState,
  type RoleKey,
} from '@/demo/data'

/** Content for `/admin/clients` — implementer firms × their clients and modules. */

export const SUPER_ADMIN_CHROME = {
  moduleLabel: 'ADMINISTRATION',
  breadcrumb: 'Clients & modules',
} as const

export const SUPER_ADMIN_HEADER = {
  title: 'Clients & modules',
  lede: 'Every implementer firm on the platform, the clients each one owns, and which per-client module is installed, merely available, or not licensed at all.',
  tableCaption: 'Clients of this firm against every per-client installable module',
} as const

/**
 * Cross-firm scope is ASPIRATIONAL. The tenancy seam exists in the runtime but
 * no organisation has ever been split — every route today is scoped to one
 * org. This surface draws the axis the product intends; it does not claim the
 * runtime has it. Hence the mandatory ConceptBadge (AC-5.3 / AC-7.1).
 */
export const ASPIRATION_NOTICE =
  'Cross-firm scope is aspirational. The tenancy seam exists in the runtime, but no organisation has ever been split: every route today is scoped to one org and the relay fails closed on a null org. This surface draws the axis the product intends; it does not claim the runtime has it. Every other surface in the prototype stays inside the anchor firm.'

export const SECOND_FIRM_NOTICE =
  'Second implementer firm. It owns clients and an installed-module set and nothing else — no projects, people, assignments or work packages are seeded for it, and no figure is invented for it. It exists here to prove the axis, and it appears on no other surface in the product.'

export const DERIVATION_NOTE = {
  before:
    'A cell is installed when the client has it, available when the firm licenses it but this client has not installed it, and not licensed when the firm does not carry it. Every cell renders one of those three — never a blank, never a number. The module list is the ',
  linkLabel: 'see the architecture map',
  after: ' per-client installable modules of the 44 in Rev A: ',
} as const

export const SUPER_ADMIN_DENSITY_NOTE =
  'Minimalist: the matrix carries state and nothing else — no seat counts, no spend, no contract value, no dates. Firm-level commercial facts live on the firm record. Nothing here is withheld that would block a decision: a not-licensed cell is stated rather than left blank, so “no data” and “not licensed” can never be confused.'

export const CELL_LEGEND: Readonly<Record<ModuleInstallState, string>> = {
  installed: 'Installed',
  available: 'Available, not installed',
  'not-licensed': 'Not licensed by the firm',
}

export const SUPER_ADMIN_LABELS = {
  cellStates: 'Cell states',
  anchorPill: 'Anchor firm · this tenant',
  secondPill: 'Second implementer · shallow',
  columns: { client: 'Client', office: 'Office', vertical: 'Vertical', odoo: 'Odoo' },
} as const

/** Short column heads for the 17 per-client module columns. */
export const MODULE_COLUMN_LABELS: Readonly<Record<string, string>> = {
  'template-library': 'Templates',
  'client-profile': 'Profile',
  'client-data-room': 'Data room',
  'client-context-graph': 'Context graph',
  'access-sharing': 'Access & sharing',
  'client-pl': 'Client P&L',
  'project-workspace': 'Workspace',
  'work-packages': 'Work pkgs',
  'hours-timesheets': 'Hours',
  deliverables: 'Deliverables',
  'consultant-assignment': 'Assignment',
  'budget-vs-actual': 'Budget vs actual',
  'margin-utilization': 'Margin & util.',
  'project-agent-crew': 'Agent crew',
  'deliverable-versioning': 'Versioning',
  'expert-agents': 'Expert agents',
  'tools-connectors': 'Tools',
}

export const matrixColumns = (): readonly ModuleEntry[] => perClientModules()

/** The per-client columns grouped by L-level, for the spanning header row. */
export const matrixColumnGroups = (): readonly { level: ModuleLevel; span: number }[] => {
  const groups: { level: ModuleLevel; span: number }[] = []
  for (const module of matrixColumns()) {
    const last = groups[groups.length - 1]
    if (last && last.level === module.level) last.span += 1
    else groups.push({ level: module.level, span: 1 })
  }
  return groups
}

export interface FirmGroup {
  firm: Firm
  isAnchor: boolean
  clients: readonly Client[]
  summary: string
  pill: string
}

export const firmGroups = (role: RoleKey): readonly FirmGroup[] =>
  visibleFirms(role).map((firm) => {
    const clients = clientsForFirm(firm.id)
    const isAnchor = firm.id === ANCHOR_FIRM_ID
    return {
      firm,
      isAnchor,
      clients,
      summary: `${firm.country} · ${firm.offices} offices · ${clients.length} clients · ${firm.installedModules.length} of ${MODULE_REGISTRY.length} platform modules licensed`,
      pill: isAnchor ? SUPER_ADMIN_LABELS.anchorPill : SUPER_ADMIN_LABELS.secondPill,
    }
  })

/** Every cell, DERIVED — no cell value is ever seeded (AC-5.3 / AC-7.2). */
export const cellState = (client: Client, module: ModuleEntry): ModuleInstallState =>
  moduleStateFor(client, module.id)
